// src/components/organisms/Navbar.js
// Rounded CTA pills, adds "Challenges", Sign Out -> /challenge/
// Shows hamburger on phones AND tablets (desktop nav only on lg+)

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  IconButton,
  Grid,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Snackbar,
} from "@mui/material";
import { useTheme } from "styled-components";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../../assets/logo-kbean.png";
import account from "../../assets/account-icon.png";
import sellArtIcon from "../../assets/icon-upload-art.png";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import useStore from "../../store/useStore";
import { handleCreateNewSubmissionDraft } from "../../utils/draftUtils";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const theme = useTheme();
  const navigate = useNavigate();
  const { userProfile, clearUserProfile } = useStore();
  const { currentUser } = useAuth();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      clearUserProfile();
      setSnackbarMessage("Successfully logged out.");
      setSnackbarOpen(true);
      navigate("/challenge/"); // go to /challenge/ after sign out
    } catch (error) {
      console.error("Error during logout:", error);
      setSnackbarMessage("Failed to logout. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleCreateChallenge = () => navigate("/challenge/create");

  const handleProfileClick = () => {
    if (userProfile?.uid) navigate(`/challenge/profile/${userProfile.uid}`);
    else {
      setSnackbarMessage("Profile information not available.");
      setSnackbarOpen(true);
    }
  };

  const handleSellArtClick = async () => {
    if (!currentUser?.uid) {
      setSnackbarMessage("Please sign in to upload your artwork.");
      setSnackbarOpen(true);
      navigate("/challenge/signin");
      return;
    }
    try {
      const newSubmissionId = await handleCreateNewSubmissionDraft({
        userId: currentUser.uid,
      });
      navigate(`/upload-artwork?submissionId=${newSubmissionId}`);
    } catch (e) {
      console.error("Failed to create submission draft:", e);
      setSnackbarMessage("Could not start upload. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleGoToChallenges = () => navigate("/challenge/");
  const toggleDrawer = (open) => () => setMenuOpen(open);

  // Mobile/Tablet drawer links
  const navLinks = userProfile ? (
    <>
      <ListItemButton onClick={handleGoToChallenges}>
        <ListItemText primary="Challenges" />
      </ListItemButton>
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
      <ListItemButton onClick={handleLogout}>
        <ListItemText primary="Sign Out" />
      </ListItemButton>
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
      {userProfile.admin && (
        <>
          <ListItemButton onClick={handleCreateChallenge}>
            <ListItemText primary="Create Challenge" />
          </ListItemButton>
          <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
        </>
      )}
      <ListItemButton onClick={handleProfileClick}>
        <ListItemText primary="Profile" />
      </ListItemButton>
      <Divider sx={{ backgroundColor: theme.colors.darkBlue }} />
    </>
  ) : (
    <>
      <ListItemButton component={Link} to="/challenge/signin">
        <ListItemText primary="Sign in" />
      </ListItemButton>
      <Divider />
      <ListItemButton onClick={handleGoToChallenges}>
        <ListItemText primary="Challenges" />
      </ListItemButton>
    </>
  );

  return (
    <Box component="header" sx={{ backgroundColor: theme.colors.darkBlue, p: 2, mb: 2 }}>
      <Grid container alignItems="center" justifyContent="space-between">
        {/* Logo */}
        <Grid item sx={{ display: "flex", alignItems: "center" }}>
          <a
            href="https://www.kbean.com"
            style={{ textDecoration: "none", display: "inline-flex" }}
            rel="noopener"
          >
            <img
              src={logo}
              alt="Kbean Logo"
              style={{ height: "60px", marginRight: "15px", marginLeft: "15px" }}
            />
          </a>
        </Grid>

        {/* Desktop nav (lg and up only) */}
        <Grid item sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 2 }}>
          {userProfile ? (
            <>
              <Button
                onClick={handleGoToChallenges}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  textTransform: "none",
                  borderRadius: "999px",
                  px: 2,
                  py: 1.25,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: theme.colors.green, boxShadow: "none" },
                }}
              >
                Challenges
              </Button>

              {/* Sell your artwork pill (solid, fully rounded, smaller text) */}
              <Button
                variant="contained"
                onClick={handleSellArtClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: theme.colors.green,
                  color: theme.colors.white,
                  textTransform: "none",
                  borderRadius: "999px",
                  px: 2,
                  py: 1.25,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: theme.colors.blue, boxShadow: "none" },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "basic-sans",
                    fontWeight: 700,
                    fontSize: { md: "0.95rem", xl: "1.05rem" }, // smaller label
                    lineHeight: 1,
                  }}
                >
                  Sell your artwork
                </Typography>
                <img src={sellArtIcon} alt="Sell Artwork" style={{ height: "24px" }} />
              </Button>

              <Button onClick={handleLogout} sx={{ color: "white", textTransform: "none" }}>
                Sign Out
              </Button>

              {userProfile.admin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateChallenge}
                  sx={{
                    backgroundColor: theme.colors.red,
                    fontFamily: "basic-sans",
                    fontSize: "1em",
                    fontWeight: "700",
                    color: theme.colors.white,
                    borderRadius: "50px",
                    padding: "8px 20px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: theme.colors.green, boxShadow: "none" },
                  }}
                >
                  Create +
                </Button>
              )}

              <IconButton onClick={handleProfileClick} aria-label="Open profile">
                <img
                  src={userProfile?.profileImage || account}
                  alt="Account"
                  style={{
                    height: "35px",
                    marginLeft: "10px",
                    marginRight: "20px",
                    borderRadius: "100%",
                    objectFit: "cover",
                  }}
                />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                onClick={handleGoToChallenges}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  textTransform: "none",
                  borderRadius: "999px",
                  px: 2,
                  py: 1.25,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: theme.colors.green, boxShadow: "none" },
                }}
              >
                Challenges
              </Button>

              <Button component={Link} to="/challenge/signin" sx={{ color: "white", textTransform: "none" }}>
                Sign in
              </Button>
            </>
          )}
        </Grid>

        {/* Hamburger button (phones + tablets; hidden on lg+) */}
        <Grid item sx={{ display: { xs: "block", lg: "none" } }}>
          <IconButton edge="end" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
            <MenuIcon sx={{ color: "white" }} />
          </IconButton>
        </Grid>
      </Grid>

      {/* Mobile/Tablet Drawer */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{ sx: { backgroundColor: theme.colors.mediumBlue, color: "white" } }}
      >
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
          <List>{navLinks}</List>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Navbar;
